import { Button } from "@/components/ui/button";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useActiveConnectedTools, useLeadTools } from "../../api/useConnectedTools";
import { ToolStatusPill } from "../ToolBits";
import { useToolRunner } from "./useToolRunner";
import type { LeadTool, ToolItemView } from "../../types";

const short = (item: ToolItemView) => {
  if (item.type === "score" && item.max) return `${item.value} / ${item.max}`;
  if (item.type === "list" || item.type === "table") return null;
  return String(item.value);
};

/** Short values only: a paragraph or a player has no place in a one-line strip. */
export function HeaderItems({ items }: { items: ToolItemView[] }) {
  return (
    <>
      {items.map((item, i) => {
        const text = ["longtext", "audio", "link"].includes(item.type) ? null : short(item);
        if (!text) return null;
        return (
          <span key={i} className="inline-flex items-center gap-1 rounded-md bg-[#f0f2f1] px-2 py-0.5 text-[12px]">
            {item.label ? <span className="text-[#5b6b64]">{item.label}</span> : null}
            <span className="font-medium text-[#051912]">{text}</span>
          </span>
        );
      })}
    </>
  );
}

function ToolStrip({ tool, leadId }: { tool: LeadTool; leadId: number | string }) {
  const runner = useToolRunner(tool.id, leadId);
  const latest = tool.records[0] ?? null;
  const actions = tool.actions.filter((a) => a.where.includes("header"));
  const items = latest?.header ?? [];
  if (!items.length && !actions.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[12px] font-medium text-[#385047]">{tool.name}</span>
      {latest ? <ToolStatusPill status={latest.status} /> : null}
      <HeaderItems items={items} />
      {actions.map((action) => (
        <Button key={action.key} variant="outline" size="sm" className="h-7" disabled={runner.isPending} onClick={() => runner.start(action)}>
          {runner.pendingKey === action.key ? "Working…" : action.label}
        </Button>
      ))}
      {runner.dialog}
    </div>
  );
}

/** The top of View Lead: whatever a tool chose to keep in sight on every tab. */
export default function LeadToolStrip() {
  const { lead } = useLeadForm() as { lead?: { id?: number | string } };
  const { data: active } = useActiveConnectedTools();
  const wanted = (active?.tools ?? []).filter((t) => t.surfaces.header);
  const { data } = useLeadTools(lead?.id, wanted.length > 0);
  if (!lead?.id || !wanted.length) return null;
  const tools = (data?.tools ?? []).filter((t) => wanted.some((w) => w.id === t.id));
  if (!tools.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {tools.map((tool) => (
        <ToolStrip key={tool.id} tool={tool} leadId={lead.id!} />
      ))}
    </div>
  );
}
